// Every read of the OPERATOR's DOM lives here.
//
// Parikara renders its sportsbook inside custom elements with OPEN shadow roots
// (<sport-mobile>, <sport-modal>, <sport-betslip>), so a selector written against
// `document` silently matches zero. The roots are open, so nothing here is a hard
// blocker — but these are THEIR class names, and a Digitain release can rename
// them without warning. Hence: every read is wrapped, and a failed probe reports
// `probeOk: false` so callers can fail QUIET (disable self-open) rather than
// noisy (pop a peek over an open coupon).
//
// Measured live 2026-07-31 at viewport 500x755:
//   .main-mobile-menu            parent doc, 53px, sticky bottom
//   .dg_bs_collapsed_mob_circle  48x48 at x=476, 24px visible (translateX(24px))
//   .dg_bs_root                  pointer-events:none — never intercepts our taps
//   .dg_bs_root--collapsed       present iff the slip is collapsed

export interface HostGeometry {
  /** False when the operator's chrome could not be read. Callers MUST disable
   * self-open in that case — see the spec's fail-quiet rule. */
  probeOk: boolean;
  /** Height of the operator's bottom tab bar, 0 when absent. */
  bottomNavHeight: number;
  /** The collapsed betslip disc's viewport rect, or null. */
  discRect: { x: number; y: number; width: number; height: number } | null;
  /** True when their betslip is open as a sheet. */
  betslipExpanded: boolean;
  /** Legs in the operator's coupon; null when unreadable. */
  couponLegs: number | null;
  /** Is the operator's sportsbook currently on screen?
   * `null` when the operator declares no root selector — i.e. "not gated",
   * which is NOT the same as false. Callers must hide only on an explicit
   * `false`, or every operator without the setting loses its widget. */
  sportsbookMounted: boolean | null;
  // Diagnostics only: the raw halves behind `sportsbookMounted`, so the on-screen
  // debug strip can say WHICH half failed ("in the DOM but hidden" vs "gone")
  // without re-implementing the probe and measuring something subtly different.
  sportsbookPresent: boolean | null;
}

export type GeometryProvider = () => HostGeometry;

export const EMPTY_GEOMETRY: HostGeometry = {
  probeOk: false,
  bottomNavHeight: 0,
  discRect: null,
  betslipExpanded: false,
  couponLegs: null,
  sportsbookMounted: null,
  sportsbookPresent: null,
};

const BOTTOM_NAV_SELECTOR = ".main-mobile-menu";
const BETSLIP_HOST = "sport-betslip";
const BS_ROOT = ".dg_bs_root";
const BS_COLLAPSED = "dg_bs_root--collapsed";
const DISC = ".dg_bs_collapsed_mob_circle";
const DISC_NUM = ".dg_bs_collapsed_mob_circle_num";

/** Is this element actually rendered, rather than merely in the document? */
function isShowing(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  // Walks the ancestors because the SPA is as likely to hide the tab's WRAPPER as
  // the element itself, and a child of a `display:none` parent still computes its
  // own `display` as normal. Depth is ~10 and this runs once a second.
  //
  // Deliberately NOT offsetParent/getBoundingClientRect: a board that is mounted
  // but still painting measures 0px for a beat, which would blink the chip off
  // every time the user enters the sport tab — and neither reads as hidden under
  // jsdom, so the guard could not be tested.
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
  }
  return true;
}

/** @param sportsbookRoot selector from `theme.sportsbook_root`; omitted → not gated. */
export function readHostGeometry(sportsbookRoot?: string): HostGeometry {
  if (typeof document === "undefined") return { ...EMPTY_GEOMETRY };

  // Polled, never cached: Parikara moves between its casino home and the
  // sportsbook as an SPA transition with NO page load (verified live — a
  // window flag set before the tab switch survived it), so a one-shot check at
  // init would be wrong for the rest of the session.
  let sportsbookMounted: boolean | null = null;
  let sportsbookPresent: boolean | null = null;
  if (sportsbookRoot) {
    try {
      const root = document.querySelector(sportsbookRoot) as HTMLElement | null;
      sportsbookPresent = root != null;
      // PRESENT IS NOT ENOUGH — it must be SHOWING. An SPA is free to keep a
      // booted tab in the DOM under `display:none` instead of unmounting it, and
      // then "the element exists" reports the sportsbook forever, which is the
      // same failure mode `window.SportFrame` was rejected for.
      //
      // `offsetParent`, not the height: a board that is mounted but still
      // painting is 0px tall for a beat, and gating on height would blink the
      // chip off every time the user enters the sport tab. `display:none` is the
      // one thing that nulls offsetParent. The rect is a fallback for the
      // position:fixed case, where offsetParent is null while visible.
      sportsbookMounted = root != null && isShowing(root);
    } catch {
      // A malformed selector must not gate the widget off — fail OPEN.
      sportsbookMounted = null;
    }
  }

  let bottomNavHeight = 0;
  let discRect: HostGeometry["discRect"] = null;
  let betslipExpanded = false;
  let couponLegs: number | null = null;
  let sawNav = false;
  let sawSlip = false;

  try {
    const nav = document.querySelector(BOTTOM_NAV_SELECTOR);
    if (nav) {
      sawNav = true;
      bottomNavHeight = Math.round(nav.getBoundingClientRect().height) || 0;
    }
  } catch {
    /* fail quiet */
  }

  try {
    const host = document.querySelector(BETSLIP_HOST) as HTMLElement | null;
    const root = host && host.shadowRoot;
    if (root) {
      const bs = root.querySelector(BS_ROOT);
      if (bs) {
        sawSlip = true;
        betslipExpanded = !bs.classList.contains(BS_COLLAPSED);

        // Scoped under `bs`, not `root` — the disc is only ever meaningful as
        // a child of the located betslip root. If Digitain renames `.dg_bs_root`
        // out from under us, `bs` is null, `sawSlip` stays false, `probeOk`
        // reports false, and the disc fields below stay at their safe
        // defaults instead of quietly leaking a plausible-looking reading.
        const disc = bs.querySelector(DISC) as HTMLElement | null;
        if (disc) {
          const r = disc.getBoundingClientRect();
          discRect = {
            x: Math.round(r.x),
            y: Math.round(r.y),
            width: Math.round(r.width),
            height: Math.round(r.height),
          };
          const num = disc.querySelector(DISC_NUM);
          const raw = (num?.textContent || "").trim();
          couponLegs = raw === "" ? 0 : Number.parseInt(raw, 10);
          if (Number.isNaN(couponLegs)) couponLegs = null;
        }
      }
    }
  } catch {
    // A throw here means their structure moved under us. Report the probe as
    // failed and leave every field at its safe default.
    // Keep the sportsbook gate even here. It is read BEFORE this try and is a
    // single querySelector; dropping it on a probe failure would restore the
    // widget on the operator's own casino page, which is the bug being fixed.
    return { ...EMPTY_GEOMETRY, bottomNavHeight, sportsbookMounted, sportsbookPresent };
  }

  return {
    probeOk: sawNav && sawSlip,
    bottomNavHeight,
    discRect,
    betslipExpanded,
    couponLegs,
    sportsbookMounted,
    sportsbookPresent,
  };
}

/** Open the operator's betslip by clicking their own collapsed disc — the same
 * element `readHostGeometry` measures for `discRect`.
 *
 * ⛔ THE ONE PLACE THE WIDGET TOUCHES THEIR UI, and only on a deliberate user
 * tap on a pill that is already promising a held pick (owner, 2026-09-01). The
 * layer's standing rule — never open or move their UI — still holds for
 * everything unprompted; see the self-open guard in renderer.showPeek.
 *
 * Returns true ONLY when a click was really dispatched, so the caller can tell
 * "opened their slip" from "their structure moved and I did nothing". Never
 * clicks an already-expanded slip: their disc is a TOGGLE, so that would close
 * the very thing the user asked to see.
 */
/** The coupon's OWN combined price, when the operator shows one. The owner's
 *  screenshot (2026-09-09) reads «Коэф.: 3.2» inside the betslip; the EN/TK
 *  shells label it "Odds" / "Koef.". Read as TEXT off the betslip's shadow root
 *  — no class is relied on — and only when exactly one such badge is present.
 *  ⛔ Returns null on any doubt: the caller keeps the last known price. */
const COUPON_TOTAL_RE = /(?:Коэф\.?|Odds|Koef\.?)\s*:?\s*(\d+[.,]\d+)/g;
export function readCouponTotal(): number | null {
  try {
    const host = document.querySelector(BETSLIP_HOST) as HTMLElement | null;
    const root = host && host.shadowRoot;
    if (!root) return null;
    const bs = root.querySelector(BS_ROOT) as HTMLElement | null;
    const text = (bs ? bs.textContent : root.textContent) || "";
    const hits = Array.from(text.matchAll(COUPON_TOTAL_RE), (m) => parseFloat(m[1].replace(",", ".")));
    if (hits.length !== 1 || !Number.isFinite(hits[0]) || hits[0] <= 1) return null;
    return hits[0];
  } catch {
    return null;
  }
}

export function openBetslip(): boolean {
  try {
    const host = document.querySelector(BETSLIP_HOST) as HTMLElement | null;
    const root = host && host.shadowRoot;
    if (!root) return false;
    const bs = root.querySelector(BS_ROOT);
    if (!bs || !bs.classList.contains(BS_COLLAPSED)) return false;
    const disc = bs.querySelector(DISC) as HTMLElement | null;
    if (!disc) return false;
    disc.click();
    return true;
  } catch {
    // Their structure moved under us. Fail quiet — exactly like the probe above.
    return false;
  }
}
